//wizard step 2 of CreateDisplayConceptModal
import React, { useState, useMemo } from 'react';
import propTypes from "prop-types";
import { Modal, Button, ProgressIndicator } from 'rhombus-react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormSyncErrors, change, reduxForm, resetSection } from 'redux-form';
import { replace } from 'connected-react-router';

import { clearLevel } from "reducers/Selected";
import { saveConcept } from "reducers/ConceptDetails";
import { loadCreatives } from 'reducers/Creatives';

import EmptySection from 'widgets/EmptySection';
import { syncErrorsSelector } from 'widgets/forms/validators/CreateErrorMessages';
import { CONCEPT_NAME } from 'forms/list';
import { getPartnerTags } from "utils/Flags";

import SelectCreativeTypeNew, { customValidate } from './SelectCreativeTypeNew';
import PartnerCreativePane from './PartnerCreativePane';

import ExistingCreativesGrid from "./open/ExistingCreativesGrid";

import { adSizes } from './CreateDisplayConceptModal';

export function customSizeValidation(value){
	const { width, height } = value;
	return width === undefined || height === undefined;
}

export function buildDisplayTagSizes(baseSizes, customSizes, currentConceptSizes={}) {
	const {
		"base-sizes": currentBaseSizes = {},
		"custom-sizes": currentCustomSizes = [],
	} = currentConceptSizes || {};

	return [
		...(baseSizes ? Object.keys(baseSizes).filter(((size) => {
			return baseSizes[size] !== false && !currentBaseSizes.hasOwnProperty(size);
		})) : []),
		...(customSizes ? customSizes.reduce((custom, size) => {
			if(!currentCustomSizes.includes(size)){
				custom.push(`${size.width}x${size.height}`);
			}
			return custom;
		}, []) : [])
	];
}

function SelectCreativeTypePane(props){
	const { Breadcrumb,
			navigationActions: { stepBack },
			commonModalProps,
			handleSubmit,
			valid,
		} = props
		, {
			existingCreatives = []
			, messages = []
			, formErrors = { errors:[]},
		} = useSelector((state) => {
			return {
				existingCreatives:state.selected?.existingCreatives,
				formErrors:{
					errors:getFormSyncErrors(CONCEPT_NAME)(state),
				},
				messages: syncErrorsSelector(CONCEPT_NAME)(state),
			};
		})
		, [isLoading, setIsLoading] = useState(false)
		, [apiErrors, setApiErrors] = useState(false)
		, { handleSizeSelected, libraryId, returnPath, values, creative, setCreative } = commonModalProps
		, dispatch = useDispatch()
		, canDance = getPartnerTags()
		;

	let partnerSelect = useMemo(() => {
		if(!canDance){ return null; }

		return (
			<div className="creative-tag" onClick={() => setCreative({ selected: true, type: 'partner-sizes' })}>
				<EmptySection
					header="Creative Tag"
					className="creative-type partner-select cypress-partner-select"
					icon="fal fa-code" />
			</div>
		);
	}, [canDance]);

	function renderContent(){
		function renderSelectionRoutes() {
			return(
				<>
					<header className="header-info">
						<strong>Select how you would like to add creatives to this concept</strong>
					</header>
					<div className="selection-routes">
						<div className="new-ad-adstudio" onClick={() => setCreative({ selected: true, type: 'new' })}>
							<EmptySection
								header="New Ad Studio Creative"
								className="creative-type ad-studio-select cypress-ad-studio-select"
								icon="fal fa-palette" />
						</div>
						{partnerSelect}
						<div className="add-existing-grid-option" onClick={() => setCreative({ selected: true, type: 'existing' })}>
							<EmptySection
								header="Add Existing Creative"
								className="creative-type existing-select cypress-existing-select"
								icon="fal fa-plus-circle" />
						</div>
					</div>
				</>
			);
		}

		function nextStep() {
			switch (creative.type) {
			case 'new':
				return (
					<SelectCreativeTypeNew
						adSizes={adSizes}
						handleSubmit={handleSubmit}
						customSizes={values?.['custom-sizes']}
						errorMessages={messages}
						handleSizeSelected={handleSizeSelected}
						libraryId={libraryId}
						setIsLoading={setIsLoading}
						returnPath={returnPath}
						formErrors={formErrors}
						formName={CONCEPT_NAME}
						headerText="Select Ad Studio placement sizes"
					/>
				);
			case 'partner-sizes':
				return (
					<SelectCreativeTypeNew
						adSizes={adSizes}
						handleSubmit={handleSubmit}
						customSizes={values?.['custom-sizes']}
						errorMessages={messages}
						handleSizeSelected={handleSizeSelected}
						libraryId={libraryId}
						setIsLoading={setIsLoading}
						returnPath={returnPath}
						formErrors={formErrors}
						formName={CONCEPT_NAME}
						headerText="Select Creative Tag placement sizes"
					/>
				);
			case 'partner-html':
				return (
					<PartnerCreativePane
						libraryId={libraryId}
						setIsLoading={setIsLoading}
						errorMessages={messages}
						formErrors={formErrors}
						setApiErrors={setApiErrors}
						formName={CONCEPT_NAME}
						handleSubmit={handleSubmit}
						valid={valid}
						handleSubmitSuccess={() => {
							dispatch(loadCreatives(libraryId));
							dispatch(replace(returnPath));
						}}
					/>
				);
			case 'existing':
				return (<ExistingCreativesGrid
					libraryId={libraryId}
					returnPath={returnPath}
				/>);
			default:
				break;
			}
		}
		return (
			<div className="c-modal-content">
				{Breadcrumb}
				<div className="c-modal body">
					<ProgressIndicator isLoading={isLoading} />
					{creative.selected ? nextStep() : renderSelectionRoutes()}
				</div>
			</div>
		);
	}

	function prevStep() {
		const { selected, type } = creative;
		dispatch(clearLevel('existingCreatives'));
		adSizes.map(fld => dispatch(change(CONCEPT_NAME, `base-sizes[${fld}]`, false)));
		dispatch(change(CONCEPT_NAME, `custom-sizes`), []);
		if(!selected) {
			setCreative({ selected: false, type: '' });
			stepBack();
		} else if(type === 'partner-html'){
			dispatch(resetSection(CONCEPT_NAME, 'display-tags'));
			setCreative({ selected: true, type: 'partner-sizes' });
		}else {
			setCreative({ selected: false, type: '' });
		}
	}

	const customSizeValResult = values?.['custom-sizes']?.some(customSizeValidation);

	function buttonValidation(){
		if(Object.keys(formErrors.errors)?.length && values?.['custom-sizes']?.length || customSizeValResult || isLoading){
			return false;
		}

		if(creative.type === 'partner-html') {
			if(!values?.['display-tags']){ return false; }
			return Object.keys(formErrors.errors).length === 0 && !apiErrors;
		}

		if(existingCreatives.length){
			return true;
		}

		if(values?.["custom-sizes"]?.length){
			return true;
		}

		if(values?.["base-sizes"]){
			return Object.entries(values["base-sizes"]).some(v => v[1]);
		}

		return false;
	}


	function renderFooter() {
		const { selected, type } = creative;
		let footer = [
			<Button
				className="back-button"
				key="back"
				theme="default"
				disabled={isLoading}
				onClick={() => prevStep()}>Back</Button>
		];

		if(!selected){ return footer; }

		if(type === 'partner-sizes'){
			footer.push(
				<Button
					key="add"
					theme="blue"
					type="button"
					onClick={() => setCreative({ selected: true, type: 'partner-html' })}
					disabled={!buttonValidation()}>Next To Add Creative Tags</Button>
			);
		}else {
			footer.push(
				<Button key="submit"
					theme="green"
					className="submit-concept cypress-submit-concept"
					type="submit"
					form={CONCEPT_NAME}
					disabled={!buttonValidation()}>Create Concept</Button>
			);
		}

		return footer;
	}

	const modalProps = {
		...commonModalProps,
		renderContent,
		renderFooter,
	};

	return <Modal {...modalProps} />;
}

export default reduxForm({
	form: CONCEPT_NAME,
	shouldValidate: () => true,
	validate: customValidate,
	destroyOnUnmount: false,
	onSubmit(values, dispatch, formProps){
		dispatch(saveConcept(formProps.libraryId, {
			...values,
		}));
	},
})(SelectCreativeTypePane);

SelectCreativeTypePane.propTypes = {
	Breadcrumb:propTypes.node.isRequired,
};
